/**
 * What the Add Invoice preview panel shows (SN-8310).
 *
 *   enter_amount  no amount yet, so nothing to price
 *   loading       the typed amount is settling, or its first price is on the way
 *   field_error   the server refused the AMOUNT itself — shown under the field
 *   error         the slot cannot take an invoice — shown in the panel
 *   ready         the server's exact charge for the amount on screen
 */
export type AddInvoicePreviewState =
  | 'enter_amount'
  | 'loading'
  | 'field_error'
  | 'error'
  | 'ready'

export interface AddInvoicePreviewInput {
  amountCents: number
  /** The amount on screen has not reached the preview yet (debounce). */
  settling: boolean
  isFetching: boolean
  hasPreview: boolean
  hasError: boolean
  /** The error is keyed on the amount, not on the slot. */
  isAmountError: boolean
}

export const addInvoicePreviewState = ({
  amountCents,
  settling,
  isFetching,
  hasPreview,
  hasError,
  isAmountError,
}: AddInvoicePreviewInput): AddInvoicePreviewState => {
  if (amountCents <= 0) {
    return 'enter_amount'
  }
  if (settling || (isFetching && !hasPreview)) {
    return 'loading'
  }
  if (hasError) {
    return isAmountError ? 'field_error' : 'error'
  }
  return hasPreview ? 'ready' : 'loading'
}

/**
 * The confirm needs the server's price for exactly the amount on screen, with no
 * refetch in flight: a refetch still holds the previous fingerprint, which the
 * server would refuse as stale.
 */
export const canConfirmAddInvoice = (
  state: AddInvoicePreviewState,
  isFetching: boolean,
  fingerprint: string | null | undefined
): boolean => state === 'ready' && !isFetching && Boolean(fingerprint)
